import { typedSupabase } from "../../lib/typed-supabase";
import type { IService } from "../service-registry";
import { BaseNotificationService, type NotificationServiceResponse } from "./base-notification.service";

/**
 * Notification State Interface
 * Handles state change operations: Mark as read, unread, etc.
 */
export interface INotificationState extends IService {
  markAsRead(notificationIds: string[]): Promise<NotificationServiceResponse<number>>;
  markAllAsRead(userId: string): Promise<NotificationServiceResponse<number>>;
  markAsUnread(notificationIds: string[]): Promise<NotificationServiceResponse<number>>;
}

/**
 * Notification State Service
 * Handles all state change operations (read/unread status)
 * Single responsibility: State management layer
 *
 * Benefits:
 * - Returns count of affected rows (useful for UI updates)
 * - Supports batch operations (same cost for 1 or 100 IDs)
 * - Automatic timestamp management (read_at field)
 * - Clear separation from CRUD and read operations
 *
 * @example
 * ```typescript
 * const result = await notificationState.markAsRead(["id1", "id2", "id3"]);
 * if (result.data) {
 *   console.log(`Marked ${result.data} notifications as read`);
 * }
 * ```
 */
class NotificationState
  extends BaseNotificationService
  implements INotificationState
{
  readonly name = "notification-state" as const;
  protected readonly serviceName = this.name;

  /**
   * Mark multiple notifications as read
   *
   * Batch operation: Marks all provided notification IDs as read in a single query.
   * Sets read_at timestamp automatically.
   *
   * @param notificationIds - Array of notification IDs to mark as read
   * @returns Count of notifications marked as read
   *
   * @example
   * ```typescript
   * // Mark single notification as read
   * const result = await notificationState.markAsRead(["notif-123"]);
   * console.log(`Marked ${result.data} as read`);
   *
   * // Mark multiple notifications in one query
   * const result = await notificationState.markAsRead([
   *   "notif-1", "notif-2", "notif-3"
   * ]);
   * console.log(`Marked ${result.data} notifications as read`);
   * ```
   */
  async markAsRead(
    notificationIds: string[]
  ): Promise<NotificationServiceResponse<number>> {
    // Validate input
    if (!notificationIds || notificationIds.length === 0) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "At least one notification ID is required",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<number>;
    }

    if (notificationIds.length > 1000) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "Cannot mark more than 1000 notifications at once",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<number>;
    }

    const startTime = performance.now();

    const response = await this.executeOperation(
      "NOTIFICATION_MARK_READ",
      async () => {
        const now = new Date().toISOString();
        const { error, data } = await typedSupabase
          .from("notifications")
          .update({ is_read: true, read_at: now })
          .in("id", notificationIds)
          .select("id");

        return {
          data: data?.length || 0,
          error,
        };
      }
    );

    if (!response.error) {
      this.logMetrics("NOTIFICATION_MARK_READ", performance.now() - startTime, {
        notificationCount: notificationIds.length,
        markedCount: response.data || 0,
      });
    }

    return response;
  }

  /**
   * Mark all notifications as read for a user
   *
   * Marks all unread notifications for a specific user as read in a single query.
   * Sets read_at timestamp automatically.
   *
   * @param userId - User ID whose notifications should be marked as read
   * @returns Count of notifications marked as read
   *
   * @example
   * ```typescript
   * const result = await notificationState.markAllAsRead("user-123");
   * if (result.data) {
   *   console.log(`Marked all ${result.data} notifications as read`);
   * }
   * ```
   */
  async markAllAsRead(userId: string): Promise<NotificationServiceResponse<number>> {
    // Validate input
    if (!userId) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "userId is required",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<number>;
    }

    const startTime = performance.now();

    const response = await this.executeOperation(
      "NOTIFICATION_MARK_ALL_READ",
      async () => {
        const now = new Date().toISOString();
        const { error, data } = await typedSupabase
          .from("notifications")
          .update({ is_read: true, read_at: now })
          .eq("user_id", userId)
          .eq("is_read", false)
          .select("id");

        return {
          data: data?.length || 0,
          error,
        };
      }
    );

    if (!response.error) {
      this.logMetrics(
        "NOTIFICATION_MARK_ALL_READ",
        performance.now() - startTime,
        {
          userId,
          markedCount: response.data || 0,
        }
      );
    }

    return response;
  }

  /**
   * Mark multiple notifications as unread
   *
   * Batch operation: Marks all provided notification IDs as unread in a single query.
   * Clears the read_at timestamp.
   *
   * @param notificationIds - Array of notification IDs to mark as unread
   * @returns Count of notifications marked as unread
   *
   * @example
   * ```typescript
   * const result = await notificationState.markAsUnread(["notif-123"]);
   * console.log(`Marked ${result.data} as unread`);
   * ```
   */
  async markAsUnread(
    notificationIds: string[]
  ): Promise<NotificationServiceResponse<number>> {
    // Validate input
    if (!notificationIds || notificationIds.length === 0) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "At least one notification ID is required",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<number>;
    }

    if (notificationIds.length > 1000) {
      return {
        data: null,
        error: {
          code: "VALIDATION_ERROR",
          message: "Cannot mark more than 1000 notifications at once",
          service: this.serviceName,
        },
      } as NotificationServiceResponse<number>;
    }

    const startTime = performance.now();

    const response = await this.executeOperation(
      "NOTIFICATION_MARK_UNREAD",
      async () => {
        const { error, data } = await typedSupabase
          .from("notifications")
          .update({ is_read: false, read_at: null })
          .in("id", notificationIds)
          .select("id");

        return {
          data: data?.length || 0,
          error,
        };
      }
    );

    if (!response.error) {
      this.logMetrics("NOTIFICATION_MARK_UNREAD", performance.now() - startTime, {
        notificationCount: notificationIds.length,
        markedCount: response.data || 0,
      });
    }

    return response;
  }
}

export const notificationState = new NotificationState();
export { NotificationState };
